<template>
    <vx-expandable-card id="custom-attributes">
        <template v-slot:header> Custom Attributes </template>
        <template v-slot:headerSubtitle> Metadata that can be used in other resources. </template>

        <template v-slot:content>
            <div class="flex flex-col gap-2 mb-3 lg:flex-row lg:mb-6">
                <div class="lg:w-60" data-test="custom-attribute-key">
                    <vx-label>Key</vx-label>
                    <vx-input
                        :disabled="!onCorporate() && !['admin', 'owner'].includes(roleOnCurrentGroup)"
                        ref="keyElement"
                        size="lg"
                        v-model="key"
                        placeholder="key"
                        :error="displayFirstError(validate.key.$errors)"></vx-input>
                </div>
                <div class="flex grow gap-2">
                    <div class="grow" data-test="custom-attribute-value">
                        <vx-label>Value</vx-label>
                        <vx-input
                            data-test="custom-attribute-value"
                            :disabled="!onCorporate() && !['admin', 'owner'].includes(roleOnCurrentGroup)"
                            size="lg"
                            placeholder="value"
                            v-model="value"
                            @keypress.enter="add()"
                            :error="displayFirstError(validate.value.$errors)"></vx-input>
                    </div>
                    <div class="pt-7 lg:pt-6">
                        <vx-button @click="add()"
                            data-test="custom-attribute-add"
                            aria-label="Add custom attribute"
                            :disabled="!onCorporate() && !['admin', 'owner'].includes(roleOnCurrentGroup)"
                            size="lg"
                            type="button">
                            <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                        </vx-button>
                    </div>
                </div>
            </div>
            <vx-alert :closeable="false"
                v-if="!model.length">
                No custom attributes were added to this group.
            </vx-alert>
            <div v-else>
                <vx-list-animated v-model="model"
                    value="key"
                    label="key" :removeable="onCorporate() || ['admin', 'owner'].includes(roleOnCurrentGroup)">
                    <template v-slot:item="{ item }">
                        <div class="grid grid-cols-1 w-full gap-1.5 px-5 py-3 text-xs text-slate-900 lg:grid-cols-2 lg:gap-2">
                            <div class="line-clamp-3 break-all font-bold lg:font-normal">{{ item.key }}</div>
                            <div class="line-clamp-3 break-all">{{ item.value }}</div>
                        </div>
                    </template>
                </vx-list-animated>
            </div>
        </template>
    </vx-expandable-card>

</template>

<script setup>
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxExpandableCard, VxInput, VxLabel, VxListAnimated } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { maxLength, requiredIf } from '@vuelidate/validators';
import { nextTick, ref, onMounted, onUnmounted } from 'vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { csvInjection, groupCustomAttributeKey, customAttributeValue, displayFirstError } from '~/utils/validation';
import { useFormSubmissionChild } from '~/composables/useFormSubmission';

const { onCorporate, roleOnCurrentGroup } = useCurrentGroup();

const model = defineModel({ default: () => []});
const keyElement = ref();

const focusKeyElement = async () => {
    await nextTick();
    keyElement.value.field?.focus();
}

const key = ref('');
const value = ref('');

const adding = ref(false);

const { onBeforeSubmit, offBeforeSubmit } = useFormSubmissionChild();

const validate = useVuelidate({
    key: {
        requiredIf: requiredIf(adding),
        maxLength: maxLength(40),
        format: groupCustomAttributeKey,
        csvInjection: csvInjection,
    },
    value: {
        requiredIf: requiredIf(adding),
        maxLength: maxLength(350),
        format: customAttributeValue,
        csvInjection: csvInjection,
    },
}, {
    key,
    value,
});

const submitPendingAttribute = async () => {
    if (adding.value) {
        return;
    }

    if (!key.value && !value.value) {
        return;
    }

    await add();
};

const add = async () => {
    adding.value = true;
    await nextTick();

    const isValid = await validate.value.$validate();

    if (!isValid) {
        setTimeout(() => {
            adding.value = false;
        }, 3500);
        return;
    }

    const existingIndex = model.value.findIndex(customAttribute => customAttribute.key.toLowerCase() === key.value.toLowerCase());
    if (existingIndex !== -1) {
        model.value.splice(existingIndex, 1);
    }

    model.value.unshift({
        key: key.value.toLowerCase(),
        value: value.value,
    });

    key.value = '';
    value.value = '';
    adding.value = false;
    focusKeyElement();
}

onMounted(() => {
    onBeforeSubmit(submitPendingAttribute);
});

onUnmounted(() => {
    offBeforeSubmit(submitPendingAttribute);
});
</script>
